import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Upload, Target, FileText, Sparkles } from 'lucide-react';
import { HeroSection } from '@/components/home/hero-section';
import { FeaturesSection } from '@/components/home/features-section';
import { HowItWorksSection } from '@/components/home/how-it-works-section';
import { PartnersSection } from '@/components/home/partners-section';
import { CtaSection } from '@/components/home/cta-section';

const SLIDE_DURATION = 8000; // 8 seconds per slide
const TRANSITION_DURATION = 800; // 0.8 seconds for transitions

const heroMessages = [
  { 
    type: 'professional',
    title: 'Plan Your Career Path',
    subtitle: 'Skip the traditional job hunt. Our AI analyzes your profile and maps your career trajectory to find your perfect next role.',
    gradient: 'from-blue-600 via-blue-500 to-indigo-500',
    cta: 'Upload Your Resume',
    icon: Upload
  },
  { 
    type: 'professional',
    title: 'AI Career Planning',
    subtitle: 'Let AI guide your career growth. Get personalized recommendations and connect with opportunities that match your aspirations.',
    gradient: 'from-indigo-600 via-purple-500 to-blue-500',
    cta: 'Start Career Planning',
    icon: Target
  },
  { 
    type: 'employer',
    title: 'Find Top Talent Instantly',
    subtitle: 'Stop sifting through resumes. Our AI matches you with candidates whose skills and career trajectories align with your needs.',
    gradient: 'from-purple-600 via-blue-500 to-indigo-500',
    cta: 'Find Candidates',
    icon: FileText
  },
  { 
    type: 'employer',
    title: 'Hire Smarter, Not Harder',
    subtitle: 'Transform your hiring process with AI-powered candidate matching, automated scheduling, and deep talent insights.',
    gradient: 'from-blue-600 via-indigo-500 to-purple-500',
    cta: 'Start Hiring',
    icon: Sparkles
  }
];

export default function HomePage() {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [nextMessage, setNextMessage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [actualUserCount, setActualUserCount] = useState(32567);
  const progressInterval = useRef<number>();
  const transitionTimeout = useRef<number>();

  useEffect(() => {
    // Progress bar animation
    const startTime = Date.now();
    progressInterval.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = (elapsed / SLIDE_DURATION) * 100;
      
      if (newProgress >= 100) {
        setProgress(0);
        setIsTransitioning(true);
        setNextMessage((currentMessage + 1) % heroMessages.length);
        
        transitionTimeout.current = window.setTimeout(() => {
          setCurrentMessage(nextMessage);
          setIsTransitioning(false);
        }, TRANSITION_DURATION);
      } else {
        setProgress(newProgress);
      }
    }, 16); // ~60fps

    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
    };
  }, [currentMessage, nextMessage]);

  // Fetch actual user count from Supabase (simulated here)
  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        // This would be replaced with an actual Supabase query
        setActualUserCount(32567);
      } catch (error) {
        console.error('Error fetching user count:', error);
      }
    };

    fetchUserCount();
  }, []);

  return (
    <div className="flex flex-col w-full min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background border-b">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
        
        <HeroSection 
          messages={heroMessages}
          currentMessage={currentMessage}
          nextMessage={nextMessage}
          isTransitioning={isTransitioning}
          progress={progress}
          userCount={actualUserCount}
        />
      </section>

      {/* Features Section */}
      <FeaturesSection />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Partners Section */}
      <PartnersSection />

      {/* CTA Section */}
      <CtaSection onUploadClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
    </div>
  );
}